import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGameSocket,
  useScoreboard,
} from '@aco/shared';
import type { GameStateSyncPayload, PlayerStats, Round, ScoringMode } from '@aco/shared';
import { getGame, patchRound, patchScoringMode } from '../services/api.js';
import { useGameContext } from '../context/GameContext.js';
import { useAuth } from '../context/AuthContext.js';
import { getPprPlayers } from '../utils/game-utils.js';

// In production, socket.io is proxied through nginx at the same origin.
// In dev (Vite), the proxy config forwards /socket.io/ to port 3201.
const REALTIME_URL = window.location.origin;

/**
 * Core game page logic: socket connection, score submission, gear menu handlers.
 * Returns all state and handlers needed by GamePage and its sub-components.
 */
export function useGamePage(gameId: string | undefined) {
  const navigate = useNavigate();
  const { state: ctxState } = useGameContext();
  const { isAuthenticated, user } = useAuth();

  const {
    game,
    team1Score,
    team2Score,
    isComplete,
    handleScoreUpdated,
    handleStateSync,
    setGame,
  } = useScoreboard();

  // Score entry overlay state
  const [entryTeam, setEntryTeam] = useState<'team1' | 'team2' | null>(null);

  // Gear menu visibility
  const [gearOpen, setGearOpen] = useState(false);

  // Authenticated users get `player:<userId>` source; anonymous get generic tablet tag
  const source = isAuthenticated && user
    ? (`player:${user.userId}` as const)
    : ('scoreboard:anonymous' as const);

  const { status: socketStatus, submitScore, externalUpdate, clearExternalUpdate } = useGameSocket({
    realtimeUrl: REALTIME_URL,
    gameId: gameId ?? '',
    source,
    onScoreUpdated: handleScoreUpdated,
    onStateSync: (payload: GameStateSyncPayload) => {
      handleStateSync(payload);
      setGame(payload.game);
    },
  });

  // On mount — load game from context or fetch it
  useEffect(() => {
    if (ctxState.game && ctxState.game.id === gameId) {
      setGame(ctxState.game);
    } else if (gameId) {
      getGame(gameId)
        .then(({ game: fetched }) => setGame(fetched))
        .catch((err) => console.error('[GamePage] Failed to load game:', err));
    }
  }, [gameId]); // intentionally run once on mount

  // ── Score entry handlers ──────────────────────────────────

  const handleScoreSquareTap = useCallback((teamId: 'team1' | 'team2') => {
    setEntryTeam(teamId);
  }, []);

  const handleScoreSubmit = useCallback(
    (team1Delta: number, team2Delta: number, playerStats?: PlayerStats[]) => {
      if (!game) return;
      submitScore({
        roundNumber: game.currentRound,
        team1Score: team1Delta,
        team2Score: team2Delta,
        playerStats,
      });
      setEntryTeam(null);
    },
    [game, submitScore],
  );

  /**
   * PPR submit handler — receives per-player stats from Advanced/Intermediate components.
   * Derives team deltas from the two players' totalPoints.
   */
  const handlePprSubmit = useCallback(
    (playerStats: PlayerStats[]) => {
      if (!game) return;
      const team1Points = playerStats[0]?.totalPoints ?? 0;
      const team2Points = playerStats[1]?.totalPoints ?? 0;
      submitScore({
        roundNumber: game.currentRound,
        team1Score: team1Points,
        team2Score: team2Points,
        playerStats,
      });
      setEntryTeam(null);
    },
    [game, submitScore],
  );

  // ── Gear menu handlers ────────────────────────────────────

  const handleToggleScoringMode = useCallback(async () => {
    if (!game) return;
    const nextMode: ScoringMode =
      game.scoringMode === 'intermediate' ? 'advanced' : 'intermediate';
    const { game: updated } = await patchScoringMode(game.id, nextMode);
    setGame(updated);
  }, [game, setGame]);

  const handleCorrectRound = useCallback(
    async (
      round: Round,
      newTeam1Score: number,
      newTeam2Score: number,
      newPlayerStats?: PlayerStats[],
    ) => {
      if (!game) return;
      try {
        const { game: updated } = await patchRound(game.id, round.roundNumber, {
          team1Score: newTeam1Score,
          team2Score: newTeam2Score,
          playerStats: newPlayerStats,
          source,
        });
        setGame(updated);
      } catch (err) {
        console.error('[handleCorrectRound] Failed to patch round:', err);
        throw err;
      }
    },
    [game, source, setGame],
  );

  /** Resets all overlay state — called on any cancel/submit in the correction flow. */
  const closeAllOverlays = useCallback(() => {
    setEntryTeam(null);
    setGearOpen(false);
  }, []);

  const handleNewGame = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // ── Derived helpers ───────────────────────────────────────

  const getPprPlayersForEntry = useCallback(
    (entryTeamId: 'team1' | 'team2') => {
      if (!game) return [];
      return getPprPlayers(game, entryTeamId);
    },
    [game],
  );

  return {
    game,
    team1Score,
    team2Score,
    isComplete,
    setGame,
    socketStatus,
    externalUpdate,
    clearExternalUpdate,
    entryTeam,
    setEntryTeam,
    gearOpen,
    setGearOpen,
    handleScoreSquareTap,
    handleScoreSubmit,
    handlePprSubmit,
    handleToggleScoringMode,
    closeAllOverlays,
    handleCorrectRound,
    handleNewGame,
    getPprPlayersForEntry,
  };
}
